/**
 * TacheForm.js — Formulaire de tâche (création + édition)
 * Design System SEIZO — Refonte UX
 * Charte : Sofia Pro / Nunito | #002335 | #00B0FF | #F4F7F9
 *
 * Changements :
 *  - Section "Emplacement" (Ligne / Colonne) supprimée
 *  - Design tiroir amélioré : layout 2 colonnes pour dates, priorité en chips pill
 *  - date_estimee pré-remplie si calNewTacheDate est défini dans le store
 */
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '../../../../stores/Context';
import Overlay from './Overlay';
import IconeStatutById from '../Icone/IconeStatutById';
import axios from 'axios';
import styles from './TacheForm.module.scss';
import { PLANNING_PRIORITIES } from '../planningPriority';

const PRIORITES = PLANNING_PRIORITIES.filter(({ value }) => value > 0)
    .map(({ value, label, color, bg }) => ({ value, label, couleur: color, bg }));
const getInitials = (prenom, nom) => `${prenom?.[0] || ''}${nom?.[0] || ''}`.toUpperCase();
const isOverdue   = (d) => d && new Date(d) < new Date();

// ── Icônes inline ─────────────────────────────────────────────────────────────
const IcoCalendar = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
);
const IcoUser = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
);
const IcoFlag = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
        <line x1="4" y1="22" x2="4" y2="15"/>
    </svg>
);
const IcoTag = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/>
        <line x1="7" y1="7" x2="7.01" y2="7"/>
    </svg>
);
const IcoPercent = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <line x1="19" y1="5" x2="5" y2="19"/>
        <circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/>
    </svg>
);
const IcoSend = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <line x1="22" y1="2" x2="11" y2="13"/>
        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
);

// ── Bloc champ avec label icône ───────────────────────────────────────────────
const FieldRow = ({ icon, label, children }) => (
    <div className={styles.fieldRow}>
        <div className={styles.fieldRowLabel}>{icon}<span>{label}</span></div>
        <div className={styles.fieldRowContent}>{children}</div>
    </div>
);

// ── Composant principal ───────────────────────────────────────────────────────
const TacheForm = observer(() => {
    const { PlanningActionsStore } = useStores();

    const [titre,          setTitre]          = useState('');
    const [description,    setDescription]    = useState('');
    const [dateDebut,      setDateDebut]      = useState('');
    const [dateEstimee,    setDateEstimee]    = useState('');
    const [priorite,       setPriorite]       = useState(2);
    const [responsableId,  setResponsableId]  = useState('');
    const [statutId,       setStatutId]       = useState('');
    const [membres,        setMembres]        = useState([]);
    const [sendingTo,      setSendingTo]      = useState(false);
    const [destinationTotemId, setDestinationTotemId] = useState('');
    const [errorMessage,   setErrorMessage]   = useState('');
    const [saving,         setSaving]         = useState(false);

    const viewModalFormAction = PlanningActionsStore.viewModalFormAction;
    const statuts             = PlanningActionsStore.statuts;
    const isCreation          = viewModalFormAction === 'création';

    // ── Chargement des membres ────────────────────────────────────────────
    useEffect(() => {
        const idPlanning = PlanningActionsStore.idPlanning;
        if (idPlanning) {
            axios.get(`${process.env.REACT_APP_API_URL}/api/PA/membres/${idPlanning}`, {
                headers: { Authorization: `Bearer ${PlanningActionsStore._token}` }
            }).then(res => setMembres(res.data.membres ?? []))
              .catch(() => setMembres([]));
        }
    }, [PlanningActionsStore.idPlanning]);

    // ── Hydratation du formulaire ─────────────────────────────────────────
    useEffect(() => {
        if (!isCreation && viewModalFormAction !== null) {
            const t = PlanningActionsStore.taches.find(t => t.id === viewModalFormAction) || PlanningActionsStore.taches_boitereception.find(t => t.id === viewModalFormAction);
            if (t) {
                setTitre(t.titre ?? '');
                setDescription(t.description ?? '');
                setDateDebut(t.date_debut ?? '');
                setDateEstimee(t.date_estimee ?? '');
                setPriorite(t.priorite ?? 2);
                setResponsableId(t.responsable_id ?? '');
                setStatutId(t.statut?.id ?? '');
            }
        } else {
            // Création : pré-remplir l'échéance depuis le calendrier si disponible
            const preDate = PlanningActionsStore.calNewTacheDate;
            setTitre('Nouvelle action');
            setDescription('');
            setDateDebut('');
            setDateEstimee(preDate || '');
            setPriorite(2);
            setResponsableId('');
            setStatutId(statuts[0]?.id ?? '');
            // Réinitialiser après usage
            if (preDate) PlanningActionsStore.calNewTacheDate = null;
        }
        setErrorMessage('');
    }, [viewModalFormAction]);

    // Les cibles sont relues à l'ouverture du tiroir. Ainsi, un changement
    // d'espace appliqué par le workflow ne laisse jamais le formulaire avec
    // une liste mémorisée vide jusqu'au prochain rechargement complet.
    useEffect(() => {
        if (!isCreation && viewModalFormAction !== null) {
            setDestinationTotemId('');
            PlanningActionsStore.refreshIntertotemActions();
        }
    }, [PlanningActionsStore, isCreation, viewModalFormAction]);

    // ── Sauvegarde ────────────────────────────────────────────────────────
    const handleValidation = async () => {
        if (!titre.trim()) { setErrorMessage('Le titre ne peut pas être vide.'); return; }
        if (!statutId)     { setErrorMessage('Vous devez sélectionner un statut.'); return; }
        setSaving(true);
        const taskData = {
            id:             isCreation ? undefined : viewModalFormAction,
            titre,
            description,
            date_estimee:   dateEstimee || null,
            date_debut:     dateDebut   || null,
            priorite:       parseInt(priorite, 10),
            responsable_id: responsableId || null,
            statut_id:      statutId,
            colonne_id:     PlanningActionsStore.celulle.colonne_id,
            sous_colonne_id:PlanningActionsStore.celulle.sousColonne_id ?? null,
            ligne_id:       PlanningActionsStore.celulle.ligne_id,
            affichage:      isCreation ? 'planning' : ((PlanningActionsStore.taches.find(t => t.id === viewModalFormAction) || PlanningActionsStore.taches_boitereception.find(t => t.id === viewModalFormAction))?.affichage ?? 'planning'),
        };
        try {
            await PlanningActionsStore.saveTask(taskData);
            PlanningActionsStore.closeModalFormAction();
        } catch (error) {
            setErrorMessage(error.response?.data?.message || "Une erreur s'est produite.");
        } finally {
            setSaving(false);
        }
    };

    const handleSendToTotem = async () => {
        if (!destinationTotemId || sendingTo) return;

        setSendingTo(true);
        setErrorMessage('');
        try {
            const tacheSource = PlanningActionsStore.taches.find(t => t.id === viewModalFormAction)
                || PlanningActionsStore.taches_boitereception.find(t => t.id === viewModalFormAction);

            if (!tacheSource) throw new Error('Action source introuvable.');

            await PlanningActionsStore.envoyerTacheVersTotem(destinationTotemId, tacheSource);
            PlanningActionsStore.closeModalFormAction();
        } catch (error) {
            setErrorMessage(error.response?.data?.message || error.message || 'Erreur lors de la transmission vers le Totem.');
        } finally {
            setSendingTo(false);
        }
    };

    const responsable = membres.find(m => m.id === Number(responsableId));
    const overdue    = isOverdue(dateEstimee);

    return (
        <Overlay
            titre={isCreation ? 'Nouvelle action' : 'Modifier l\'action'}
            onClose={() => PlanningActionsStore.closeModalFormAction()}
            width="460px"
        >
            <div className={styles.form}>

                {/* ── Erreur ─────────────────────────────────────────────── */}
                {errorMessage && <div className={styles.errorMsg}>{errorMessage}</div>}

                {/* ── Titre ──────────────────────────────────────────────── */}
                <div className={styles.titleBlock}>
                    <input
                        className={styles.titleInput}
                        type="text"
                        value={titre}
                        onChange={e => setTitre(e.target.value)}
                        placeholder="Titre de l'action…"
                        autoFocus
                    />
                </div>

                {/* ── Description ────────────────────────────────────────── */}
                <textarea
                    className={styles.descInput}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="Ajouter une description…"
                    rows={2}
                />

                <div className={styles.divider} />

                {/* ── Priorité ───────────────────────────────────────────── */}
                <FieldRow icon={<IcoFlag />} label="Priorité">
                    <div className={styles.prioriteGroup}>
                        {PRIORITES.map(p => (
                            <button
                                key={p.value}
                                className={`${styles.prioriteBtn} ${priorite === p.value ? styles.prioriteBtnActive : ''}`}
                                style={priorite === p.value
                                    ? { background: p.bg, borderColor: p.couleur, color: p.couleur }
                                    : {}}
                                onClick={() => setPriorite(p.value)}
                                type="button"
                            >
                                <span className={styles.prioriteDot} style={{ background: p.couleur }} />
                                {p.label}
                            </button>
                        ))}
                    </div>
                </FieldRow>

                {/* ── Statut ─────────────────────────────────────────────── */}
                <FieldRow icon={<IcoTag />} label="Statut">
                    <div className={styles.statutGroup}>
                        <IconeStatutById IdStatut={statutId} />
                        <select
                            className={styles.selectField}
                            value={statutId}
                            onChange={e => setStatutId(e.target.value)}
                        >
                            <option value="" disabled>Sélectionner un statut</option>
                            {statuts.map(s => (
                                <option key={s.id} value={s.id}>{s.libelle}</option>
                            ))}
                        </select>
                    </div>
                </FieldRow>

                {/* ── Dates ──────────────────────────────────────────────── */}
                <FieldRow icon={<IcoCalendar />} label="Dates">
                    <div className={styles.datesGrid}>
                        <div className={styles.dateField}>
                            <span className={styles.dateLabel}>Début</span>
                            <input
                                className={styles.dateInput}
                                type="date"
                                value={dateDebut}
                                onChange={e => setDateDebut(e.target.value)}
                            />
                        </div>
                        <div className={styles.dateField}>
                            <span className={`${styles.dateLabel} ${overdue ? styles.dateLabelOverdue : ''}`}>
                                Échéance
                                {overdue && <span className={styles.overdueTag}>En retard</span>}
                            </span>
                            <input
                                className={`${styles.dateInput} ${overdue ? styles.dateInputOverdue : ''}`}
                                type="date"
                                value={dateEstimee}
                                onChange={e => setDateEstimee(e.target.value)}
                            />
                        </div>
                    </div>
                </FieldRow>

                {/* ── Responsable ────────────────────────────────────────── */}
                <FieldRow icon={<IcoUser />} label="Responsable">
                    <div className={styles.responsableGroup}>
                        {responsable && (
                            <div className={styles.avatar}>
                                {getInitials(responsable.prenom, responsable.nom)}
                            </div>
                        )}
                        <select
                            className={styles.selectField}
                            value={responsableId}
                            onChange={e => setResponsableId(e.target.value)}
                        >
                            <option value="">— Aucun responsable —</option>
                            {membres.map(m => (
                                <option key={m.id} value={m.id}>{m.prenom} {m.nom}</option>
                            ))}
                        </select>
                    </div>
                </FieldRow>


                {/* ── Transmettre à un Totem : toujours visible en édition ─────────── */}
                {!isCreation && (
                    <div className={styles.sendToBlock}>
                        <div className={styles.sendToLabel}>
                            <IcoSend /> Transmettre cette action à un Totem
                        </div>
                        {PlanningActionsStore.intertotemActionsLoading ? (
                            <p className={styles.sendToUnavailable}>Recherche des Totems destinataires…</p>
                        ) : PlanningActionsStore.intertotemTargets.length === 0 ? (
                            <p className={styles.sendToUnavailable}>
                                Aucun autre Totem n’est actuellement rattaché au même espace.
                            </p>
                        ) : (
                            <div className={styles.sendToRow}>
                                <select
                                    className={styles.selectField}
                                    value={destinationTotemId}
                                    onChange={e => setDestinationTotemId(e.target.value)}
                                    disabled={sendingTo}
                                >
                                    <option value="">Sélectionner un Totem…</option>
                                    {PlanningActionsStore.intertotemTargets.map(totem => (
                                        <option key={totem.id_totem} value={totem.id_totem}>
                                            {totem.tot_nom || 'Totem sans nom'}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    className={styles.sendToBtn}
                                    type="button"
                                    onClick={handleSendToTotem}
                                    disabled={sendingTo || !destinationTotemId}
                                >
                                    {sendingTo ? 'Envoi…' : 'Transmettre'}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* ── Actions ────────────────────────────────────────────────────── */}
                <div className={styles.formActions}>
                    <button
                        className={styles.cancelBtn}
                        type="button"
                        onClick={() => PlanningActionsStore.closeModalFormAction()}
                    >
                        Annuler
                    </button>
                    <button
                        className={styles.saveBtn}
                        type="button"
                        onClick={handleValidation}
                        disabled={saving}
                    >
                        {saving ? 'Enregistrement…' : isCreation ? "Créer l'action" : 'Enregistrer'}
                    </button>
                </div>       </div>
        </Overlay>
    );
});

export default TacheForm;
